import { Injectable } from '@angular/core';
import { AsyncSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { AuthUsecase, DistinctSubject, recursiveQuery } from '@daikin-tic/dxone-com-lib';

import { DeviceSetCreateParams, DeviceSets } from '../models/device-set.model';
import { DeviceSetGateway } from '../usecases/device-set.gateway';
import { DeviceSetUsecase } from '../usecases/device-set.usecase';

@Injectable()
export class DeviceSetInteractor extends DeviceSetUsecase {
  override get deviceSets$(): Observable<DeviceSets> {
    return this._deviceSets;
  }

  private _deviceSets: DistinctSubject<DeviceSets> = new DistinctSubject(new DeviceSets());

  constructor(
    private _authUsecase: AuthUsecase,
    private _deviceSetGateway: DeviceSetGateway,
  ) {
    super();
    this._authUsecase.authState$
      .pipe(
        map(({ status }) => status === 'signedIn'),
        distinctUntilChanged(),
      )
      .subscribe(signedIn => (signedIn ? this.onSignIn() : this.onSignOut()));
  }

  override reload(): void {
    this.onSignIn();
  }

  override createDeviceSet(params: DeviceSetCreateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._deviceSetGateway.createDeviceSet(params).subscribe({
      next: createdDeviceSet => this._deviceSets.next(this._deviceSets.value.set(createdDeviceSet)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result;
  }

  override deleteDeviceSet(deviceSetId: string): Observable<never> {
    const result = new AsyncSubject<never>();
    this._deviceSetGateway.deleteDeviceSet(deviceSetId).subscribe({
      next: () => this._deviceSets.next(this._deviceSets.value.delete(deviceSetId)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result;
  }

  private onSignIn(): void {
    recursiveQuery(params => this._deviceSetGateway.listDeviceSets(params), {}).subscribe(deviceSets => {
      this._deviceSets.next(new DeviceSets(deviceSets));
    });
  }

  private onSignOut(): void {
    this._deviceSets.next(new DeviceSets());
  }
}
